// Cari Defter (Excel benzeri tablo) ortak yardımcıları — satır düzleştirme, renk paleti, toplamlar

export const ROW_COLORS = [
  { key: "", label: "Renk yok", bg: "", dot: "#E2E8F0" },
  { key: "yellow", label: "Sarı", bg: "#FEF9C3", dot: "#FACC15" },
  { key: "green", label: "Yeşil", bg: "#DCFCE7", dot: "#22C55E" },
  { key: "blue", label: "Mavi", bg: "#DBEAFE", dot: "#3B82F6" },
  { key: "red", label: "Kırmızı", bg: "#FEE2E2", dot: "#EF4444" },
  { key: "orange", label: "Turuncu", bg: "#FFEDD5", dot: "#F97316" },
  { key: "purple", label: "Mor", bg: "#EDE9FE", dot: "#8B5CF6" },
  { key: "gray", label: "Gri", bg: "#F1F5F9", dot: "#94A3B8" },
];
export const colorBg = (key) => (ROW_COLORS.find((c) => c.key === key) || ROW_COLORS[0]).bg;
export const colorHex = (key) => {
  const bg = colorBg(key);
  if (!bg) return null;
  return [parseInt(bg.slice(1, 3), 16), parseInt(bg.slice(3, 5), 16), parseInt(bg.slice(5, 7), 16)];
};

export const STATUS = {
  teslim: { label: "Teslim Edildi", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", rgb: [22, 163, 74] },
  bekliyor: { label: "Bekliyor", cls: "bg-amber-50 text-amber-700 border-amber-200", rgb: [217, 119, 6] },
};
export const statusOf = (it) => (it?.status === "bekliyor" ? "bekliyor" : "teslim");

export const UNITS = ["ADET", "METRE", "KİLO", "PAKET", "KUTU", "TOP", "RULO", "METRETÜL"];

const inRange = (d, range) => {
  const ds = String(d || "").slice(0, 10);
  if (range?.start && ds && ds < range.start) return false;
  if (range?.end && ds && ds > range.end) return false;
  return true;
};

// cari_entries (satış defteri) → düz satır listesi (S.N. artan, tarih → oluşturma sırası)
export function flattenLedger(entries, range) {
  const list = [...(entries || [])]
    .filter((e) => e.kind !== "alis" && inRange(e.sort_date || e.date, range))
    .sort((a, b) => String(a.sort_date || a.date || "").localeCompare(String(b.sort_date || b.date || "")) || (Number(a.seq || 0) - Number(b.seq || 0)) || String(a.created_at || "").localeCompare(String(b.created_at || "")));
  const rows = [];
  list.forEach((e) => {
    (e.items || []).forEach((it, idx) => {
      const list_price = Number(it.unit_price || 0);
      const disc = Number(it.discount_rate || 0);
      const qty = Number(it.quantity || 0);
      const net = list_price * (1 - disc / 100);
      rows.push({
        key: `${e.id}:${idx}`, entryId: e.id, idx, entry: e, it, sn: rows.length + 1,
        date: e.date, quoteNo: e.quote_no || "", name: it.name || "", code: it.code || "", description: it.description || "",
        qsId: e.quick_sale_id || "", qsNo: e.quick_sale_no || (e.quick_sale_id ? `HS-${String(e.quick_sale_id).slice(0, 6).toUpperCase()}` : ""), qsPay: e.payment_method || "",
        unit: it.unit || "ADET", quantity: qty, list_price, disc, net_price: net, tutar: net * qty,
        status: statusOf(it), color: it.color || "", by: e.created_by || "", productId: it.product_id || "", linkedNoStock: !!it.linked_no_stock,
      });
    });
  });
  return { rows, entries: list };
}

export function ledgerTotals(entries, payments, range, kdvOn = true) {
  const net = entries.reduce((s, e) => s + Number(e.net || 0), 0);
  const kdv = entries.reduce((s, e) => s + Number(e.kdv || 0), 0);
  const total = entries.reduce((s, e) => s + Number(e.total || 0), 0);
  const pays = (payments || []).filter((p) => p.direction === "gelen" && p.source !== "invoice" && inRange(p.date, range))
    .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const paid = pays.reduce((s, p) => s + Number(p.amount || 0), 0);
  const billed = kdvOn ? total : net;
  return { net, kdv, total: billed, paid, open: billed - paid, payments: pays };
}

export const matchRow = (r, q) => {
  if (!q) return true;
  const s = q.toLocaleLowerCase("tr");
  return [r.name, r.code, r.description, r.quoteNo, r.unit].some((v) => String(v || "").toLocaleLowerCase("tr").includes(s));
};

export const METHOD_LABEL = { nakit: "Nakit", kredi_karti: "Kredi Kartı", havale: "Havale/EFT", cek: "Çek" };

const r2 = (x) => Math.round(((Number(x) || 0) + 1e-9) * 100) / 100;
// Sunucudaki quote_totals ile aynı: kalem düzenlenince toplamlar sunucuyu beklemeden anında güncellenir
export const recalcEntry = (e) => {
  const sub = (e.items || []).reduce((s, it) => { const l = Number(it.quantity || 0) * Number(it.unit_price || 0); return s + l - l * (Number(it.discount_rate || 0) / 100); }, 0);
  const net = sub * (1 - Number(e.global_discount_rate || 0) / 100);
  const kdv = net * (Number(e.kdv_rate ?? 20) / 100);
  return { ...e, subtotal: r2(sub), discount: r2(sub - net), net: r2(net), kdv: r2(kdv), total: r2(net + kdv) };
};

// Defter dışı bakiye: ödenmemiş (kesilmiş) satış faturaları — cari bakiye ile defter bakiyesi arasındaki farkı açıklar
// Resmî fatura her zaman KDV dahil tutarla açık kalır (açık hesap caride de) — sunucu cari bakiyesiyle aynı kural
export function unpaidInvoices(invoices) {
  const fromLedger = (i) => (i.ledger_moved || []).length > 0 && !i.ledger_restored;   // defterden aktarılan taslak da bakiyede sayılır
  return (invoices || []).filter((i) => i.type === "satis" && (!i.is_draft || fromLedger(i)) && !i.ledger_skip && !(i.qnb || {}).cancelled
      && ((i.qnb || {}).type_code || i.type_code) !== "IADE")
    .map((i) => {
      const paid = (i.payment_history || []).reduce((s, h) => s + Number(h.amount || 0), 0);
      const no = i.invoice_no || (i.qnb || {}).invoice_no || (i.is_draft ? "Taslak fatura (defterden)" : "Fatura");
      return { id: i.id, no, date: i.date, total: r2(Number(i.total || 0)), paid: r2(paid), remaining: r2(Number(i.total || 0) - paid) };
    })
    .filter((x) => x.remaining > 0.005)
    .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
}

// Ürün koduna göre stoktan ürün bul (tam kod eşleşmesi öncelikli; tek sonuç varsa o)
export async function lookupByCode(api, code) {
  const c = String(code || "").trim();
  if (!c) return null;
  try {
    const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(c)}`);
    const list = (Array.isArray(data) ? data : []).map((r) => r.product || r);
    const lc = c.toLocaleLowerCase("tr");
    return list.find((p) => String(p.code || "").toLocaleLowerCase("tr") === lc)
      || list.find((p) => String(p.barcode || "").toLocaleLowerCase("tr") === lc || (p.barcodes || []).some((b) => String(b).toLocaleLowerCase("tr") === lc))
      || (list.length === 1 ? list[0] : null);
  } catch { return null; }
}
